/**
 * Starter — one catalog record with three faces (artifact / system /
 * governance) and a ladder position. The unification of
 * `DASHBOARD_TEMPLATES`, the Seed Registry's content, and the folder
 * products' "start with" hints. See
 * docs/plans/2026-06-11-starter-registry-plan.md (the model) and
 * docs/plans/2026-06-11-starter-registry-implementation-plan.md (PR-A).
 *
 * Lives in core (not dashboard) because it is consumed cross-surface:
 * the Create New strip, prompt matching, empty-state heroes, and the
 * editor first-save banner — same posture as src/core/seeds/.
 */

import type { AddOnId } from '../../domains/access/models/Entitlements'
import type { PostHogOrgFlagKey } from '../../domains/feature-flags/models/FeatureFlags'

/** Mirrors NATIVE_DIAGRAM_TYPES (dashboard/utils/diagramHelpers) — defined
 *  locally so WORKER code (e.g. the engagement partner-pack hook) can
 *  import starter data without chaining client modules into the worker
 *  TS project. The unions are identical and mutually assignable. */
type NativeDiagramType = 'genogram' | 'flowchart' | 'org' | 'network'

/** Whose work this starter belongs to — the catalog's primary axis and
 *  the revenue map (function section ↔ revenue line is exact). */
export type FunctionTag =
  | 'operations'
  | 'people'
  | 'it-cloud'
  | 'risk-compliance'
  | 'clinical'
  | 'strategy'
  | 'projects'
  | 'software'
  | 'general'

/** What job the user is doing — secondary filter within a function. */
export type JobTag =
  | 'map-process'
  | 'chart-org'
  | 'model-cloud'
  | 'assess-risk'
  | 'map-family'
  | 'plan-strategy'
  | 'run-project'
  | 'ideate'
  | 'design-system'
  | 'document'

/** The urgency moment a trigger-shaped starter is named for. */
export type TriggerTag =
  | 'audit-due'
  | 'reorg'
  | 'incident'
  | 'cost-review'
  | 'new-hire'
  | 'new-sop'
  | 'new-client'
  | 'case-intake'
  | 'planning-cycle'

/** The viral loop this starter feeds (what makes user #1 invite user #2). */
export type CollabAffordance =
  | 'share-view'
  | 'assign-owners'
  | 'embed'
  | 'external-review'

/**
 * The 60-second-proof mechanism. Four wire shapes:
 *  - `seed`     → Seed Registry provider, hydrated client-side
 *  - `agent`    → one agent turn (prompt start), RDM materialized client-side
 *  - `template` → a native domain template card that opens seedless to
 *                 the domain's picker/wizard; generic `template:` seeds
 *                 still exist in StartIntent, but the starter catalog no
 *                 longer owns an untested server-clone path
 *  - `legacy`   → legacy-app template clone (grandfathered; replacement queue)
 */
export type StarterArtifact =
  | { kind: 'seed'; seedId: string; diagramType: NativeDiagramType }
  | { kind: 'agent'; promptTemplate: string; diagramType: NativeDiagramType }
  | { kind: 'template'; templateDiagramId: string; diagramType: NativeDiagramType }
  | { kind: 'legacy'; templateId: string; legacyType: string }

export interface Starter {
  id: string
  name: string
  description: string
  thumbnailUrl?: string
  translationKey?: string
  artifact: StarterArtifact
  job: JobTag[]
  function: FunctionTag[]
  /** Popular rail membership (curated, not derived). */
  popular?: boolean
  /** Flowchart/org policy: the card navigates SEEDLESS so the editor's
   *  Start picker shows; the seedId still serves the system face
   *  (empty-state heroes deep-link it directly). */
  pickerOnOpen?: boolean
  /** The SYSTEM this artifact is the face of (= revenue line). */
  growsInto?: AddOnId
  trigger?: TriggerTag
  collaborative?: CollabAffordance[]
  requiredFlag?: PostHogOrgFlagKey
  /** Governance face: creately = default catalog; partner = pack-shipped
   *  (P4) — pack starters also carry `packId` + a rollout `requiredFlag`. */
  curatedBy: 'creately' | 'partner'
  /** Partner pack membership (curatedBy: 'partner'). */
  packId?: string
  /** Legacy-replacement protocol: set on a legacy card when its native
   *  successor ships; the kill rule decides when the legacy card drops. */
  replacedBy?: string
}

/** A Create New section key: the Popular rail, the org-curated shelf
 *  (governance face), plus the 8 function sections ('general' starters
 *  surface inside Projects & Ideation). */
export type StarterSection = 'popular' | 'org' | Exclude<FunctionTag, 'general'>

/** 'strategy' | 'system_design' are TRACKING lines from day one (verticals
 *  #6/#7, canon 2026-06-11) — attribution starts before the add-ons are
 *  sellable. AddOnId extends when their commercial motion lands. */
export type RevenueLine = AddOnId | 'strategy' | 'system_design' | 'workspace'

export const revenueLineOf = (s: Starter): RevenueLine => {
  if (s.growsInto) return s.growsInto
  if (s.function.includes('strategy')) return 'strategy'
  if (s.function.includes('software')) return 'system_design'
  return 'workspace'
}
